import {
  IonButton,
  IonButtons,
  IonCol,
  IonContent,
  IonFooter,
  IonGrid,
  IonHeader,
  IonIcon,
  IonLabel,
  IonModal,
  IonRow,
  IonSegment,
  IonSegmentButton,
  IonText,
  IonTitle,
  IonToolbar,
} from "@ionic/react";
import { closeOutline } from "ionicons/icons";
import React, { Fragment, useContext, useEffect, useState } from "react";
import AppPrefContext from "../../../components/contexts/AppPrefContext";
import { each } from "underscore";
import { v4 as uuidv4 } from "uuid";
import { CapacitorPreferencesConstants } from "../../../components/Constants";
import config from "../../../components/helpers/socket/config";
import {
  removeCapacitorPreference,
  setCapacitorPreference,
} from "../../../utils/core-utils/capPreferencesUtils";
import { send } from "../../../utils/sockets/asyncSocket";
import { CMEDPermissions } from "../MarketData/MarketDataContent";
import OrderEntryModal from "../MarketData/OrderEntryModal";
import optionsGridConfig from "./optionsGridConfig";
import { OrdersType } from "../../../Constants";
import Skeleton__OptionsGridViewModal from "./Skeleton__OptionsGridViewModal";

/**
 * @author
 * E45597 Aryamitra Chaudhuri
 */

type OptionsGridViewModalProps = {
  isOptionsGridViewModalOpen: boolean;
  setIsOptionsGridViewModalOpen: Function;
  selectedOption: any;
  strike: string;
  optionGridData: any;
  userCMEDEntitlements: CMEDPermissions | undefined;
  orderEntryData: any;
  orderEntryData_Live: any;
  openOrderEntryModal: boolean;
  setOpenOrderEntryModal: Function;
  orderEntryType: any;
  setOrderEntryType: Function;
  type: any;
  setType: Function;
  greeksData: any;
  notATM?: boolean;
};

const OptionsGridViewModal: React.FC<OptionsGridViewModalProps> = ({
  isOptionsGridViewModalOpen,
  setIsOptionsGridViewModalOpen,
  selectedOption,
  strike,
  userCMEDEntitlements,
  orderEntryData,
  orderEntryData_Live,
  openOrderEntryModal,
  setOpenOrderEntryModal,
  orderEntryType,
  setOrderEntryType,
  type,
  setType,
  greeksData,
  notATM,
}) => {
  const appPref = useContext(AppPrefContext);

  const [callDepth, setcallDepth] = useState<any>();
  const [putDepth, setputDepth] = useState<any>();
  const [priceType, setpriceType] = useState(undefined);

  const termStructureCode = selectedOption?.TermStructureCode;
  const strikeStr = strike?.toString();
  const isLoading = !callDepth || !putDepth || !orderEntryData || !greeksData;

  function setOptionGridDepth(res: any) {
    const callDepthArr: any[] = [];
    const putDepthArr: any[] = [];

    if (res.payload.isSnapshot) {
      each(res.payload.data, (row) => {
        if (row.o === 1) {
          callDepthArr.push(row);
        } else {
          putDepthArr.push(row);
        }
      });
      setcallDepth(callDepthArr);
      setputDepth(putDepthArr);
    } else {
      const callDepthNew: any[] = [];
      const putDepthNew: any[] = [];

      each(res.payload.data, (row) => {
        if (row.o === 1) {
          callDepthNew.push(row);
        } else {
          putDepthNew.push(row);
        }
      });

      setcallDepth((prevObjects: any) => {
        if (!prevObjects) return callDepthNew;
        const updatedObjects = [...prevObjects];
        updatedObjects.forEach((p: any, index1: any) => {
          callDepthNew.forEach((q: any) => {
            if (p.id === q.id) {
              updatedObjects[index1] = q;
            }
          });
        });
        return updatedObjects;
      });

      setputDepth((prevObjects: any) => {
        if (!prevObjects) return putDepthNew;
        const updatedObjects = [...prevObjects];
        updatedObjects.forEach((p: any, index1: any) => {
          putDepthNew.forEach((q: any) => {
            if (p.id === q.id) {
              updatedObjects[index1] = q;
            }
          });
        });
        return updatedObjects;
      });
    }
  }

  let subRequestForOptionsGridGroupItemDepth = {
    action: optionsGridConfig.socket.actions.CMED_SUBSCRIBE_STRIKE_LADDER_DEPTH,
    type: OrdersType.OPTIONS_GRID_GROUP_ITEM_DEPTH,
    requestID: uuidv4(),
    payload: {
      termStructureCode: termStructureCode,
      strike: strikeStr,
      levels: "3",
    },
  };

  let unSubRequestForOptionsGridGroupItemDepth = {
    action:
      optionsGridConfig.socket.actions.CMED_UNSUBSCRIBE_STRIKE_LADDER_DEPTH,
    type: OrdersType.OPTIONS_GRID_GROUP_ITEM_DEPTH,
    requestID: uuidv4(),
    payload: {
      termStructureCode: termStructureCode,
    },
  };

  useEffect(() => {
    if (isOptionsGridViewModalOpen) {
      setcallDepth(undefined);
      setputDepth(undefined);
      PubSub.subscribe(
        optionsGridConfig.socketKeys.CMED_SUPPLEMENTAL_DEPTH,
        async (msg, data) => {
          setOptionGridDepth(data);
        },
      );
    }
  }, [isOptionsGridViewModalOpen]);

  useEffect(() => {
    if (isOptionsGridViewModalOpen && appPref?.isAppActive) {
      if (termStructureCode && strikeStr) {
        send(
          config.socket.channels.TRADING,
          subRequestForOptionsGridGroupItemDepth,
        );
      }
    }
  }, [strikeStr, isOptionsGridViewModalOpen, appPref?.isAppActive]);

  useEffect(() => {
    if (isOptionsGridViewModalOpen) {
      if (termStructureCode) {
        setCapacitorPreference(
          CapacitorPreferencesConstants.OPTIONS_GRID_TSCODE,
          termStructureCode,
        );
      }
      if (strikeStr) {
        setCapacitorPreference(
          CapacitorPreferencesConstants.OPTIONS_GRID_STRIKE,
          strikeStr,
        );
      }
    }
    return () => {
      PubSub.unsubscribe(optionsGridConfig.socketKeys.CMED_SUPPLEMENTAL_DEPTH);
    };
  }, [strikeStr, isOptionsGridViewModalOpen]);

  const currentData =
    type === "call"
      ? orderEntryData_Live?.data?.[0]
      : orderEntryData_Live?.data?.[1];

  const currentGreeks =
    greeksData && greeksData.length > 1
      ? type === "call"
        ? greeksData[0]
        : greeksData[1]
      : {};

  return (
    <>
      <IonModal isOpen={isOptionsGridViewModalOpen}>
        <IonHeader className="ion-no-border">
          <IonToolbar>
            <IonTitle>
              <p className="md-modal-header-text">CONTRACT DETAILS</p>
            </IonTitle>
            <IonButtons slot="end">
              <IonButton
                onClick={() => {
                  setIsOptionsGridViewModalOpen(false);
                  if (termStructureCode) {
                    send(
                      config.socket.channels.TRADING,
                      unSubRequestForOptionsGridGroupItemDepth,
                    );
                    removeCapacitorPreference(
                      CapacitorPreferencesConstants.OPTIONS_GRID_TSCODE,
                    );
                  }
                  if (strikeStr) {
                    removeCapacitorPreference(
                      CapacitorPreferencesConstants.OPTIONS_GRID_STRIKE,
                    );
                  }
                }}
              >
                <IonIcon
                  icon={closeOutline}
                  color="dark"
                  slot="icon-only"
                ></IonIcon>
              </IonButton>
            </IonButtons>
          </IonToolbar>
        </IonHeader>
        {isLoading ? (
          <Skeleton__OptionsGridViewModal />
        ) : (
          <Fragment>
            <IonSegment
              value={type}
              onIonChange={(e: any) => setType(e.target.value)}
            >
              <IonSegmentButton value="call">
                <IonLabel>CALL</IonLabel>
              </IonSegmentButton>
              <IonSegmentButton value="put">
                <IonLabel>PUT</IonLabel>
              </IonSegmentButton>
            </IonSegment>
            <section className="md-contract-header">
              <div>
                <div className="md-contract-name-container">
                  <p
                    style={{
                      margin: 0,
                      fontWeight: "bold",
                      fontSize: "var(--one-font-size-15)",
                    }}
                  >
                    {selectedOption?.TermStructureDescription}
                  </p>
                </div>
                <div className="md-contract-symbol-container">
                  <p className="md-contract-symbol">
                    Strike: {orderEntryData?.data[0]?.s?.f}
                  </p>
                </div>
              </div>
            </section>
            <IonContent>
              <main className="overview-container">
                <section className="md-depth-section">
                  <div className="md-grid-header-row">
                    {["QTY", "BID", "OFFER", "QTY"].map((elem, id) => (
                      <div
                        key={id}
                        className={`md-grid-header-cell ${
                          id === 1 || id === 3 ? "align-end" : "align-start"
                        }`}
                      >
                        <p className="md-grid-header-text">{elem}</p>
                      </div>
                    ))}
                  </div>
                  {Array.from({ length: 3 }).map((_, index) => {
                    const depthData = type === "call" ? callDepth : putDepth;
                    const item = depthData?.[index];
                    return (
                      <div
                        key={index}
                        className={`md-depth-row ${
                          index % 2 === 0 ? "bg-trans" : "bg-alt"
                        }`}
                      >
                        {[
                          { val: item?.bq, align: "flex-start" },
                          { val: item?.b, align: "flex-end" },
                          { val: item?.op, align: "flex-start" },
                          { val: item?.oq, align: "flex-end" },
                        ].map((col, i) => (
                          <div
                            key={i}
                            className="md-depth-cell"
                            style={{ justifyContent: col.align }}
                          >
                            <p className="md-depth-cell-text">
                              {col.val?.r !== 0 ? col.val?.f : "\u00A0"}
                            </p>
                          </div>
                        ))}
                      </div>
                    );
                  })}
                </section>
                <section className="md-stats-section">
                  <div className="md-stats-title-row">
                    <div className="md-stats-title-cell">
                      <p className="md-grid-header-text">MARKET STATISTICS</p>
                    </div>
                  </div>
                  {[
                    {
                      l1: "HIGH",
                      v1: currentData?.h?.f,
                      l2: "VOLUME",
                      v2: currentData?.tv?.f,
                    },
                    {
                      l1: "LOW",
                      v1: currentData?.ltp?.f,
                      l2: "CHANGE",
                      v2: currentData?.n?.f,
                      isChange: true,
                    },
                    {
                      l1: "OPEN",
                      v1: currentData?.o?.f || currentData?.ot?.f,
                      l2: "LAST TRADE",
                      v2: currentData?.l?.f,
                    },
                    {
                      l1: "SETTLE",
                      v1: currentData?.sp?.f,
                      l2: "OPEN INTEREST",
                      v2: currentData?.oi?.f,
                    },
                    {
                      l1: "CURRENT SETTLE",
                      v1: currentData?.cs?.f,
                      l2: "NET CHANGE",
                      isNetChange: true,
                      v2: (() => {
                        const n = currentData?.n?.r;
                        const l = currentData?.l?.r;
                        if (
                          typeof n !== "number" ||
                          typeof l !== "number" ||
                          !Number.isFinite(n) ||
                          !Number.isFinite(l)
                        ) {
                          return "-";
                        }
                        const denom = Math.abs(l - n);
                        if (denom === 0) {
                          return "-";
                        }
                        const calc = n / denom;
                        return Number.isFinite(calc)
                          ? (calc * 100).toFixed(2) + "%"
                          : "-";
                      })(),
                      color: (() => {
                        const n = currentData?.n?.r;
                        const l = currentData?.l?.r;
                        if (
                          typeof n !== "number" ||
                          typeof l !== "number" ||
                          !Number.isFinite(n) ||
                          !Number.isFinite(l)
                        ) {
                          return "";
                        }
                        const denom = Math.abs(l - n);
                        if (denom === 0) {
                          return "";
                        }
                        const val = n / denom;
                        return Number.isFinite(val) && val > 0
                          ? "success"
                          : Number.isFinite(val) && val < 0
                            ? "danger"
                            : "";
                      })(),
                    },
                    {
                      l1: "DELTA",
                      v1: currentGreeks?.d?.f,
                      l2: "THEO",
                      v2: currentGreeks?.t2?.f,
                    },
                    {
                      l1: "VOL",
                      v1: currentGreeks?.v?.f,
                    },
                  ].map((row, index) => (
                    <div
                      key={index}
                      className={`md-stats-row ${
                        index % 2 === 0 ? "bg-trans" : "bg-alt"
                      }`}
                    >
                      <div className="md-stats-label-cell">
                        <p className="md-stats-label-text">{row.l1}</p>
                      </div>
                      <div className="md-stats-value-cell">
                        <p className="md-stats-value-text">{row.v1 || "-"}</p>
                      </div>
                      {row.l2 && (
                        <>
                          <div className="md-stats-label-cell">
                            <p className="md-stats-label-text">{row.l2}</p>
                          </div>
                          <div className="md-stats-value-cell">
                            <p className="md-stats-value-text">
                              {row.isNetChange ? (
                                <IonText color={row.color}>
                                  {Number.isFinite(parseFloat(row.v2)) &&
                                  parseFloat(row.v2) > 0
                                    ? "+"
                                    : ""}
                                  {Number.isFinite(parseFloat(row.v2))
                                    ? row.v2
                                    : "-"}
                                </IonText>
                              ) : row.isChange ? (
                                <IonText
                                  color={
                                    currentData?.n?.r < 0
                                      ? "danger"
                                      : currentData?.n?.r > 0
                                        ? "success"
                                        : ""
                                  }
                                >
                                  {currentData?.n?.r > 0 ? "+" : ""}
                                  {row.v2 || "-"}
                                </IonText>
                              ) : (
                                row.v2 || "-"
                              )}
                            </p>
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                </section>
              </main>
            </IonContent>
            <IonFooter className="ion-no-padding ion-no-border md-footer-container">
              <IonGrid className="ion-padding">
                <IonRow>
                  <IonCol size="6">
                    <IonButton
                      className="custom_cmed_mobile_button"
                      expand="block"
                      fill="solid"
                      color="primary"
                      onClick={() => {
                        setOrderEntryType("bid");
                        setOpenOrderEntryModal(true);
                      }}
                      disabled={!currentData}
                    >
                      NEW BID
                    </IonButton>
                  </IonCol>
                  <IonCol size="6">
                    <IonButton
                      className="custom_cmed_mobile_button"
                      expand="block"
                      fill="solid"
                      color="danger"
                      onClick={() => {
                        setOrderEntryType("offer");
                        setOpenOrderEntryModal(true);
                      }}
                      disabled={!currentData}
                    >
                      NEW OFFER
                    </IonButton>
                  </IonCol>
                </IonRow>
              </IonGrid>
            </IonFooter>
          </Fragment>
        )}
      </IonModal>
      <OrderEntryModal
        type="options"
        orderEntryType={orderEntryType}
        openOrderEntryModal={openOrderEntryModal}
        setOpenOrderEntryModal={setOpenOrderEntryModal}
        selectedOption={selectedOption}
        orderEntryData={currentData}
        userCMEDEntitlements={userCMEDEntitlements}
        componentusedIn="optionsGrid"
        priceType={priceType}
        setpriceType={setpriceType}
        orderEntryData_Live={
          type === "call"
            ? orderEntryData_Live?.data?.[0]
            : orderEntryData_Live?.data?.[1]
        }
      />
    </>
  );
};

export default OptionsGridViewModal;
